<template>

  <div id="bg">
  <div id="wrapper">

    <!-- Nav and Login Button -->
    <LandingHeader page="home" />

    <!-- Left Section -->
    <div class="left">

      <!-- Title and Subtitle -->
      <div class="heading">
        <div class="title-container">
          <vue-typed-js
          :strings='["Surveys","CX","Data Collection","NPS Surveys","Interviews","Customer Feedback","Employee Engagement"]'
          :backSpeed="25"
          :loop="true">
            <h1 v-if="is_mobile" class="title">Run <span class="desktop-only">your </span> <span class="typing"></span><br/>on <span id="whatsapp">Whatsapp</span><p class="whatsapp-logo"></p></h1>
            <h1 v-else class="title">Run <span class="desktop-only">your </span> <span class="typing"></span><br/>on <span id="whatsapp">Whatsapp</span><p class="platforms-logo"></p></h1>
          </vue-typed-js>
        </div>
        <h2 class="subtitle">Start collecting answers from your respondents over their favorite platform.</h2>
        <p class="media-types">🎬 Video&nbsp;&nbsp;&nbsp;&nbsp;📷 Photo&nbsp;&nbsp;&nbsp;&nbsp;🎙Audio&nbsp;&nbsp;&nbsp;&nbsp;📊 Multi-Choice&nbsp;&nbsp;&nbsp;&nbsp;✏️ Text</p>
      </div>

      <!-- CTA Buttons -->
      <div class="above-fold-button-container">
        <button class="demo-btn" @click="goToSignUp">Request Demo</button>
        <button class="product-tour-btn" @click="goToSignUp">Get Started</button>
      </div>

    </div>

    <div class="right">
      <Animation />
    </div>

  </div>


  <!-- Falabella Case Study -->
  <div id="falabella-section" class="feature">
    <h2>We helped <span>Falabella</span> 10x their review collection</h2>
    <h3>Falabella is the largest department store chain in South America and a publicly listed company on the Santiago Stock Exchange (Chile). We partnered with them to transform their customer feedback process using WhatsApp.</h3>
    <div class="falabella-video-container">
      <video src="../assets/landing/falabella.mp4" autoplay muted loop playsinline controls />
    </div>
    <div class="falabella-logo-container">
      <img src="../assets/landing/falabella-logo.png" alt="Falabella" />
    </div>
  </div>


  <!-- WhatsApp Benefits -->
  <div id="whatsapp-features" class="feature">
    <h2>Experience the benefits of building on <span>WhatsApp</span>,<br/>the world's most popular and beloved platform.</h2>
    <div class="container">
      <img src="../assets/landing/whatsapp-globe.png" />
      <div>
        <div class="feature-box">
          <h4><span>🌏</span>2,000,000,000+ users</h4>
          <p>1 in 3 people worldwide use WhatsApp on a daily basis. Don’t ask people to download yet another app when they already have something they love using.</p>
        </div>
        <div class="feature-box">
          <h4><span>😄️</span>WhatsApp > Web</h4>
          <p>WhatsApp is where respondents feel most comfortable sharing their thoughts and opinions. Meet your respondents where there want to be met.</p>
        </div>
      </div>
      <div>
        <div class="feature-box">
          <h4><span>🏃‍♂️</span>Faster response time</h4>
          <p>Don’t get buried in someone's email inbox, get replied to immediately by going straight to their WhatsApp inbox. Go from a turnaround of hours to minutes.</p>
        </div>
        <div class="feature-box">
          <h4><span>🙋‍♀️</span>Higher response rate</h4>
          <p>Get response rates of up to >95%. Stop making respondents context switch between email links and clunky websites and talk to them where they already are.</p>
        </div>
      </div>
    </div>
  </div>



  <!-- Multimedia Features -->
  <div id="multimedia-features" class="feature">
    <h2>Get deeper insights from <span>videos</span>, <span>photos</span> and <span>audio</span></h2>
    <h3>Quantitative answers get you the “what” but not the “why.” Combine multi-choice answers with video and audio to get deeper insights by letting people express themselves more fully.</h3>
     <img v-if="!is_mobile" src="../assets/landing/multimedia-collage.png" />
     <img v-else src="../assets/landing/multimedia-collage-mobile.png" />
  </div>




  <!-- Dashboard Features -->
  <div id="dashboard-features" class="feature">
    <h2>All your responses in one <span>simple</span> dashboard.</h2>
    <img src="../assets/landing/dashboard.png" />

    <h3>Packed with Features</h3>

    <div class="container">
      <div>
        <div class="feature-box">
          <h4><span>✍️</span>Automatic Transcriptions</h4>
          <p>No more manually transcribing answers, get automatic transcriptions for all your audio and video answers.</p>
        </div>
        <div class="feature-box">
          <h4><span>📱️</span>Mobile Responsive</h4>
          <p>Work on the go with our fully responsive dashboard.</p>
        </div>
        <div class="feature-box">
          <h4><span>🔔</span>Push over WhatsApp</h4>
          <p>Initiate the conversation directly over WhatsApp, no more sending links in emails or SMS.</p>
        </div>
        <div class="feature-box">
          <h4>🧠<span></span>Conditional Logic</h4>
          <p>Only want to ask certain questions to respondents who have answered a certain way? It's possible with our smart question builder.</p>
        </div>
      </div>
      <div>
        <div class="feature-box">
          <h4><span>🤖</span>API Access</h4>
          <p>Access your survey, answer and respondent data though our REST API. Setup a webhook to get live updates when new answers come in.</p>
        </div>
        <div class="feature-box">
          <h4><span>🕐️</span>Real-time Answers</h4>
          <p>Watch answers roll in in real-time. See an always up-to-date state of your responses.</p>
        </div>
        <div class="feature-box">
          <h4><span>‍🗝️</span>SSO for easy access</h4>
          <p>Use Google Single Sign-On to get your whole team on the same page easily and securely.</p>
        </div>
        <div class="feature-box">
          <h4><span>‍📛️</span>White-labeled bots</h4>
          <p>Use your company name and logo with your verified WhatsApp bot to reassure your respondents that they're talking to you.</p>
        </div>
      </div>
      <div>
        <div class="feature-box">
          <h4><span>🗄️</span>Export your data</h4>
          <p>Move your data to whatever tool you prefer by exporting to CSV or JSON. It's your data, after all.</p>
        </div>
        <div class="feature-box">
          <h4><span>🤝</span>Made for Teams</h4>
          <p>Collaborate with teammates - like, comment and share noteworthy responses.</p>
        </div>
        <div class="feature-box">
          <h4><span>🈯</span>Multi-language Support</h4>
          <p>Converse with respondents in whatever language they're most comfortable in.</p>
        </div>
      </div>
    </div>

    <br/>
    <br/>
    <h3>Coming Soon</h3>

    <div class="container">
      <div>
        <div class="feature-box">
          <h4><span>💡</span>AI-Powered Insights</h4>
          <p>Get insights automatically surfaced by our built-in analytics.</p>
        </div>
      </div>
      <div>
        <div class="feature-box">
          <h4><span>🙋‍♀️️</span>Follow-up Questions</h4>
          <p>Dive deeper into interesting answers with follow-up questions for any respondent you choose.</p>
        </div>
      </div>

    </div>
  </div>


  <!-- Survey Creator Features -->
  <div id="survey-creator-features" class="feature">
    <div class="text-container">
      <h2>Powerful<br/>conversation<br/>builder.</h2>
      <p>Our conversation builder lets you script powerful interviews without writing a single line of code. It works on your phone too!</p>
      <p>Multi-language support, interview your respondents in whatever language they’re most comfortable in.</p>
    </div>
    <div class="image-container">
      <img src="../assets/landing/survey-creator.png"/>
    </div>
  </div>


  <!-- Demo Video Section -->
  <!-- <div id="demo-video">
    <center>
      <h2>Check out our full product walkthrough</h2>
      <div class="loom-container">
        <div style="position: relative; padding-bottom: 55.81395348837209%; height: 0;"><iframe src="https://www.loom.com/embed/49cbcfbce08949bebaddcb5876854a4f" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>
      </div>
    </center>
  </div> -->

  <!-- Founders Section -->
  <div id="founders-section" class="feature">
    <h2>Meet the <span>Founders</span></h2>
    <h3>We started Outside Voice because we believed the future of customer feedback lives where your customers already are — on WhatsApp.</h3>
    <div class="founders-video-container">
      <video src="../assets/landing/founders.mp4" autoplay muted loop playsinline controls />
    </div>
  </div>


  <!-- Bottom CTA -->
  <div id="bottom-demo-form-container">
    <h2><span>Interested?</span> Get in touch</h2>
    <div class="bottom-cta-buttons">
      <button class="demo-btn" @click="goToSignUp">Request a Demo</button>
      <button class="product-tour-btn" @click="goToSignUp">Get Started</button>
    </div>
  </div>


  <!-- Investors Section -->
  <div id="investors">
    <!-- <h2>Supported By</h2> -->
    <a id="facebook-accelerator" target="_blank" href="https://markets.businessinsider.com/news/stocks/facebook-accelerator-singapore-introduces-14-startups-in-season-2-1029046767#"></a>
    <a id="tech-in-asia"         target="_blank" href="https://www.techinasia.com/whatsapp-incubating-regions-billiondollar-startup"></a>
    <a id="ef"                   target="_blank" href="https://www.techinasia.com/entrepreneur-first-largest-deep-tech-startup-cohort"></a>
    <a id="iterative"            target="_blank" href="https://iterative.vc/posts/iterative-summer-2020-batch"></a>
  </div>




  <LandingFooter />

  </div>


</template>

<script>

import { isMobile, isMobileOnly } from 'mobile-device-detect';

import LandingHeader from '@/components/landing/LandingHeader.vue';
import LandingFooter from '@/components/landing/LandingFooter.vue';
import Animation from '@/components/landing/Animation.vue';

export default {

  name: 'landing',
  components: {
    LandingHeader,
    LandingFooter,
    Animation
  },

  data: function () {
    return {}
  },

  computed: {
    is_mobile() {
      return isMobileOnly;
    }
  },

  methods: {

    goToSignUp() {
      analytics.track('Get Started Clicked', { page: 'landing' });
      this.$router.replace('/signup');
    }

  }

}
</script>


<style lang="scss" scoped>

body{
  font-family: 'Montserrat', sans-serif;
}

#bg{
  display: block;
  width: 100%;
  min-height: 100%;
  height: auto;
  background-image:
    url("../assets/landing-world-bg.png"),
    url("../assets/landing-whatsapp-bg.png");
  background-position:
    center 180px,
    top left;
  background-repeat:
    no-repeat,
    repeat;
  overflow: hidden;
}


#wrapper{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 1050px;
  height: 100%;
  padding-top: 100px;
  margin: 0 auto;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  .left, .right{
    //display: inline-block;
  }
  .left{
    width: 55%;
    padding-top: 100px;
  }
  .right{
    width: 45%;
  }
}

.heading{
  margin-top: 7%;
  margin-bottom: 10px;
  .title-container{
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: baseline;
  }
  &>*{
    display: inline-block;
  }
  .title, .subtitle{
    color: white;
  }
  .title{
    font-size: 34px;
    font-weight: 600;
    white-space: nowrap;
    color: white !important;
    span#whatsapp{
      font-weight: 700;
    }
    p{
      display: inline-block;
    }

  }
  .subtitle{
    font-size: 22px;
    line-height: 36px;
    font-weight: 500;
    width: 80%;
  }
  .media-types{
    color: white;
    font-size: 17px;
    line-height: 40px;
    font-weight: 500;
    margin-top: 5px;
  }
  .whatsapp-logo{
    height: 46px;
    width: 46px;
    background-image: url("../assets/whatsapp-logo.svg");
    background-size: contain;
    background-repeat: no-repeat;
    margin: 0px;
    margin-left: 15px;
    transform: translateY(10px);
  }
  .platforms-logo{
    height: 46px;
    width: 320px;
    background-image: url("../assets/platforms-landing.png");
    background-size: contain;
    background-repeat: no-repeat;
    margin: 0px;
    margin-left: 15px;
    transform: translateY(10px);
  }
}

h2{
  font-size: 32px;
  font-weight: 500;
  opacity: .9;
}

.demo-btn{
  color: #003122;
  font-size: 16px;
  font-weight: 700;
  width: 250px;
  padding: 12px;
  margin-top: 25px;
  background-color: #26E2A9;
  border: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  &:hover{
    background-color: #00FFC1;
  }
}

.product-tour-btn{
  color: white;
  font-size: 16px;
  font-weight: 700;
  width: 220px;
  padding: 12px;
  margin-top: 25px;
  margin-left: 25px;
  background-color: #07171E;
  border: 2px solid white;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: .8;
  &:hover{
    opacity: 1;
    //background-color: #00FFC1;
  }
}


.spinner{
  display: block;
  margin: 0 auto;
  margin-top: 50px;
  .ant-spin-dot i{
    background-color: #00FFC1 !important;
  }
}


.feature{
  width: 1050px;
  margin: 0 auto;
  font-family: 'Montserrat', sans-serif;

  h2{
    color: white;
    font-size: 28px;
    font-weight: 700;
    line-height: 44px;
    margin-bottom: 50px;
    span{
      color: #00E7A5;
    }
  }
  .container{
    display: flex;
    & > * {
      flex: 2;
    }
    img{
      flex: 1;
      width: 300px;
      align-self: flex-start;
    }
    .feature-box{
      background-color: #172C34;
      border-radius: 6px;
      padding: 20px;
      margin-bottom: 30px;
      margin-left: 30px;
      h4,p{
        color: white;
      }
      h4{
        font-size: 16px;
        font-weight: 600;
        span{
          margin-right: 10px;
          font-size: 20px;
        }
      }
      p{
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
      }
    }
  }
}


#falabella-section{
  margin-top: 80px;
  text-align: center;
  h2{
    margin-bottom: 20px;
  }
  h3{
    color: rgba(255,255,255,0.8);
    font-size: 17px;
    font-weight: 400;
    line-height: 30px;
    width: 75%;
    margin: 0 auto;
    margin-bottom: 40px;
  }
  .falabella-video-container{
    width: 80%;
    margin: 0 auto;
    video{
      width: 100%;
      border-radius: 10px;
      &:focus{
        outline: none;
      }
    }
  }
  .falabella-logo-container{
    margin-top: 30px;
    img{
      height: 40px;
      opacity: 0.8;
    }
  }
}

#whatsapp-features{
  margin-top: 110px;
  img{
    margin-right: 15px;
  }
}


#multimedia-features{

  margin-top: 120px;
  h2, h3{
    text-align: center;
  }
  h2{
    margin-bottom: 20px;
  }
  h3{
    color: white;
    font-size: 18px;
    font-weight: 500;
    line-height: 32px;
    width: 85%;
    margin: 0 auto;
    margin-bottom: 50px;
  }
  img{
    width: 100%;
  }
}


#push-features{

  display: flex;
  margin-top: 150px;
  margin-bottom: 150px;

  .text-container{
    flex: 1;
    width: 300px;
    padding-right: 60px;
    h2{
      font-size: 34px;
      line-height: 55px;
      margin-top: 60px;
      margin-bottom: 25px;
    }
    p{
      color: white;
      font-size: 17px;
      line-height: 28px;
      margin-bottom: 30px;
    }
  }
  .video-container{
    flex: 2;
    video{
      width: 90%;
      float: right;
      border-radius: 10px;
      &:focus{
        outline: none;
      }
    }
  }

}


#dashboard-features{
  margin-top: 150px;
  h2,h3{
    text-align: center;
    margin-bottom: 25px;
  }
  h3{
    font-size: 19px;
    font-weight: 400;
    color: white;
  }
  img{
    width: 100%;
    margin-bottom: 40px;
  }
  .container{
    .feature-box{
      margin-left: 20px;
      margin-right: 20px;
    }
  }

}


#investors{
  display: flex;
  width: 1050px;
  margin: 0 auto;
  margin-top: 110px;
  font-family: 'Montserrat', sans-serif;
  h2{
    color: white;
    font-size: 28px;
    font-weight: 600;
    margin-right: 44px;
  }
  & > a{
    background-repeat: no-repeat;
    background-position: top left;
    height: 90px;
    margin-right: 40px;
    transition: all 0.2s ease;
    opacity: .85;
    &:hover{
      opacity: 1;
    }
  }
  #facebook-accelerator{
    width: 400px;
    background-size: 390px;
    background-image: url("../assets/landing/facebook-accelerator.svg");
  }
  #tech-in-asia{
    width: 250px;
    background-size: 250px;
    background-image: url("../assets/landing/tech-in-asia.png");
    background-repeat: no-repeat;
  }
  #ef{
    width: 90px;
    background-size: 90px;
    background-image: url("../assets/landing/ef.svg");
  }
  #iterative{
    width: 160px;
    background-size: 160px;
    background-image: url("../assets/landing/iterative.svg");
  }
}



#survey-creator-features{

  display: flex;
  margin-top: 150px;
  margin-bottom: 150px;

  .text-container{
    flex: 1;
    width: 300px;
    padding-right: 40px;
    h2{
      font-size: 34px;
      line-height: 55px;
      margin-top: 60px;
    }
    p{
      color: white;
      font-size: 17px;
      line-height: 28px;
      margin-bottom: 30px;
    }
  }
  .image-container{
    flex: 2;
    img{
      width: 100%;
    }
  }
}

#founders-section{
  margin-top: 120px;
  text-align: center;
  h2{
    margin-bottom: 20px;
  }
  h3{
    color: rgba(255,255,255,0.8);
    font-size: 17px;
    font-weight: 400;
    line-height: 30px;
    width: 65%;
    margin: 0 auto;
    margin-bottom: 40px;
  }
  .founders-video-container{
    width: 70%;
    margin: 0 auto;
    video{
      width: 100%;
      border-radius: 10px;
      &:focus{
        outline: none;
      }
    }
  }
}

#bottom-demo-form-container{
  width: 500px;
  margin: 0 auto;
  margin-top: 100px;
  margin-bottom: 40px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  h2{
    color: white;
    font-size: 28px;
    font-weight: 700;
    line-height: 44px;
    text-align: center;
    margin-bottom: 40px;
  }
  .bottom-cta-buttons{
    display: flex;
    justify-content: center;
    gap: 20px;
  }
}

#product-tour-modal{
  video{
    width: 100%;
  }
}


#demo-video{
  margin-top: 80px;
  margin-bottom: 160px;
  h2{
    width: 80%;
    color: white;
    font-size: 28px;
    font-weight: 700;
    line-height: 44px;
    font-family: 'Montserrat', sans-serif;
    margin-bottom: 50px;
  }
  video{
    width: 750px;
    border-radius: 8px;
  }
  .loom-container{
    width: 750px;
  }

}



@media only screen
and (min-device-width: 320px)
and (max-device-width: 480px) {

#bg{
  background-image: url("../assets/landing-whatsapp-bg.png");
  background-position: top left;
  background-repeat: repeat;
}

#wrapper{
  width: 88%;
  padding-top: 85px;
  flex-direction: column;
  .right, .left{
    width: 100%;
  }
}

.heading{
  .title-container{
    margin-top: 25px;
    justify-content: center;
  }
  margin-top: 10px;
  .title,
  .subtitle{

  }
  .title{
    display: inline;
    text-align: center;
    font-size: 31px !important;
    line-height: 47px !important;
    margin-bottom: 10px;
    white-space: normal;
    span.desktop-only{
      display: none;
    }
  }
  .subtitle{
    display: none;
    text-align: center;
    margin-top: 8px;
    font-size: 18px !important;
    line-height: 28px !important;
    width: 100%;
  }
  .whatsapp-logo{
    display: inline-block;
    height: 37px;
    width: 37px;
    background-size: 37px;
    margin-left: 10px;
    transform: translateY(7px);
  }
  .media-types{
    display: none;
  }
}

.demo-btn, .product-tour-btn{
  display: block;
  margin: 0 auto;
  margin-top: 30px;
  margin-bottom: 20px;
}


.feature{
  width: 85%;
  h2{
    font-size: 22px;
    font-weight: 700;
    line-height: 34px;
    margin-bottom: 30px;
  }
}


.container{
  flex-direction: column;
  img{
    margin-bottom: 50px;
  }
  .feature-box{
    margin-left: 0px;
  }
}

#falabella-section{
  margin-top: 50px;
  h3{
    width: 90%;
    font-size: 15px;
    line-height: 24px;
  }
  .falabella-video-container{
    width: 95%;
  }
}

#whatsapp-features{

  margin-top: 80px;
  .container{
    flex-direction: column;
    img{
      margin-bottom: 50px;
    }
    .feature-box{
      margin-left: 0px;
    }
  }

}

#multimedia-features{
  h3{
    display: none;
  }
}

#push-features{
  flex-direction: column;
  margin-top: 40px;
  .text-container{
    padding-right: 0px;
    width: 100%;
    h2{
      font-size: 22px;
      line-height: 32px;
      text-align: center;
      width: 80%;
      margin-left: 10%;
    }
    p{
      font-size: 16px;
      line-height: 22px;
      text-align: center;
    }
  }
}

#dashboard-features{
  margin-top: 60px;
  .container{
    .feature-box{
      margin-left: 0px;
      margin-right: 0px;
    }
  }
}


#survey-creator-features{
  margin-top: 20px;
  margin-bottom: 80px;
  flex-direction: column;
  .text-container{
    padding-right: 0px;
    h2{
      font-size: 22px;
      line-height: 32px;
      text-align: center;
      width: 80%;
      margin-left: 10%;
    }
    p{
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }
}


#investors{
  width: 90%;
  flex-direction: column;

  h2{
    margin: 0 auto;
    margin-bottom: 20px;
  }
  & > div{
    margin: 0 auto;
    background-size: 100% !important;
  }
  #facebook-accelerator{
    background-size: 80%;
  }
}


#demo-video{
  margin-top: 20px;
  margin-bottom: 120px;
  h2{
    font-size: 22px;
    line-height: 32px;
    margin-bottom: 30px;
  }
  video{
    width: 85%;
  }
  .loom-container{
    width: 90%;
  }
}

#founders-section{
  margin-top: 60px;
  h3{
    width: 90%;
    font-size: 15px;
    line-height: 24px;
  }
  .founders-video-container{
    width: 95%;
  }
}

#bottom-demo-form-container{
  width: 88%;
  margin-left: 6%;
  h2{
    font-size: 22px;
    margin-bottom: 20px;
    span{
      display: none;
    }
  }
}


}

</style>
